<template>
  <div class="text-center">
    <v-dialog
      v-model="dialog"
      width="75vw"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          color="primary"
          v-bind="attrs"
          v-on="on"
          rounded
        >
          Manage Attendees
        </v-btn>
      </template>

      <v-card shaped>
        <v-card-title class="headline grey lighten-2">
          <v-row>
            <v-col>
              Manage Attendees
            </v-col>
            <v-col class="text-right">
              <v-btn
                rounded
                text
                @click="dialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row class="pt-5">
              <v-col class="pt-0">
                <div class="d-flex justify-space-between align-center">
                  <h4 class="text-uppercase">
                    Add Attendees
                  </h4>
                  <v-checkbox
                    v-model="sendInviteEmail"
                    dense
                    label="send invitiation email"
                  ></v-checkbox>
                </div>
                <v-autocomplete
                  v-model="employeesToAdd"
                  :items="attendeesOptions"
                  chips
                  small-chips
                  multiple
                  label="Add People"
                  solo
                  rounded
                  persistent-hint
                  hint="People you select will be added to the session regardless of other selections made"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="remove(data.item, 'employeesToAdd')"
                    >
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ data.item.title }} &bullet; {{ data.item.dept }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <v-card elevation="2" class="pa-5" shaped>
                  <v-switch
                    class="pt-0 mt-0"
                    v-model="includeContingentWorkers"  
                    inset
                    label="Include Contingent Workers"
                  ></v-switch>
                  <v-switch
                    class="pt-0 mt-0"
                    v-model="includeManagersOnly"  
                    inset
                    label="Include Managers Only"
                  ></v-switch>
                  <v-autocomplete
                    v-model="departmentsToAdd"
                    :items="departmentOptions"
                    chips
                    small-chips
                    multiple
                    label="Add Departments"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="remove(data.item, 'departmentsToAdd')"
                      >
                        {{ data.item.text }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                    v-model="locationsToAdd"
                    :items="locationOptions"
                    chips
                    small-chips
                    multiple
                    label="Add Locations"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="remove(data.item, 'locationsToAdd')"
                      >
                        {{ data.item.text }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                    v-model="otherGroupsToAdd"
                    :items="otherGroupOptions"
                    chips
                    small-chips
                    multiple
                    label="Add Other Groups"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="remove(data.item, 'otherGroupsToAdd')"
                      >
                        {{ data.item.text }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-card>
              </v-col>
              <v-col class="shrink">
                <v-dialog
                  v-model="previewDialog"
                  width="768"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mt-5 mb-5"
                      block
                      rounded
                      v-bind="attrs"
                      v-on="on"
                      :disabled="saveDisabled"
                    >
                      Preview
                      <span v-if="attendeesToAdd.length > 0" class="ml-2">
                        {{ attendeesToAdd.length }}
                      </span>
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-text>
                      <EmployeeTable 
                        :elevation="0"
                        title="Attendees To Add"
                        :hideActions="true"
                        :employees="attendeesToAddEmployeeRecords"
                        :headerProps="[
                          { text: 'Title', align: 'start', sortable: true, value: 'fields.Title' },
                          { text: 'Department', align: 'start', sortable: true, value: 'fields.Department' },
                          { text: 'Location', align: 'start', sortable: true, value: 'fields.Location Formatted' }
                        ]"
                      />
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        rounded
                        @click="previewDialog = false"
                      >
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-btn
                  class="mb-5"
                  color="primary"
                  rounded
                  block
                  :loading="saveLoading"
                  @click="save"
                  :disabled="saveDisabled"
                >
                  Add 
                  <span v-if="attendeesToAdd.length > 0" class="ml-2">
                    {{ attendeesToAdd.length }}
                  </span>
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
                <v-btn
                  color="error"
                  block
                  rounded
                  :loading="removeLoading"
                  @click="removeAttendees"
                  :disabled="removeDisabled"
                >
                  <v-icon left>mdi-arrow-left</v-icon>
                  Remove
                  <span v-if="employeesToRemove.length > 0" class="ml-2">
                    {{ employeesToRemove.length }}
                  </span>
                </v-btn>
              </v-col>
              <v-col class="pt-0">
                <div class="d-flex justify-space-between align-center">
                  <h4 class="text-uppercase">
                    <v-badge
                      color="primary"
                      inline
                      :content="attendees.length"
                    >
                      Attendees
                    </v-badge>
                  </h4>
                  <v-checkbox
                    style="visibility: hidden;"
                    dense
                    label="here for spacing alignment only"
                  ></v-checkbox>
                </div>
                <v-list shaped dense avatar>
                  <v-list-item-group
                    v-model="employeesToRemove"
                    multiple
                  >
                    <template v-for="attendee in attendees">
                      <v-list-item
                        :key="attendee.id"
                        :value="attendee"
                        active-class="error--text text--accent-4"
                      >
                        <template v-slot:default="{ active, toggle }">
                          <v-list-item-avatar>
                            <v-img :src="getAvatar(attendee)"></v-img>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title v-text="attendee.fields.Name">
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ attendee.fields.Title }} &bullet; 
                              {{ attendee.fields.Department}}
                            </v-list-item-subtitle>
                          </v-list-item-content>

                          <v-list-item-action>
                            <v-checkbox
                              :input-value="active"
                              :true-value="attendee"
                              color="error accent-4"
                              @click="toggle"
                            ></v-checkbox>
                          </v-list-item-action>
                        </template>
                      </v-list-item>
                    </template>
                  </v-list-item-group>
                </v-list>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>  
  import moment from 'moment'
  import EmployeeTable from '@/components/employee/Table'
  
  export default {
    name: 'AddAttendeeGroup',
    props: {
      session: Object,
      employees: Array
    },
    components: {
      EmployeeTable
    },
    data () {
      return { 
        employeesToRemove: [],
        employeesToAdd: [],
        departmentsToAdd: [],
        includeContingentWorkers: false,
        includeManagersOnly: false,
        locationsToAdd: [],
        otherGroupsToAdd: [],
        otherGroupOptions: [
          {
            text: 'Did Not Attend This Curriculum',
            value: 'Did Not Attend This Curriculum'
          },
          { 
            text: 'Requires Manager Training', 
            value: 'Requires Manager Training'
          },
          {
            text: 'Requires Diversity Training',
            value: 'Requires Diversity Training'
          },
          {
            text: 'Requires Sexual Harassment Training',
            value: 'Requires Sexual Harassment Training'
          },
          {
            text: 'Requires COVID-19 Training',
            value: 'Requires COVID-19 Training'
          }
        ],
        dialog: false,
        previewDialog: false,
        saveLoading: false,
        removeLoading: false,
        disableSave: false,
        disableRemove: false,
        sendInviteEmail: false
      }
    },
    computed: {
      attendees () {
        return this.employees.filter(e => {
          if (e.fields.Sessions) {
            return e.fields.Sessions.includes(this.session.id)
          }
        }).sort((a,b) => {
          return a.fields.Name > b.fields.Name ? 1 : -1
        })
      },
      attendeesOptions () {
        return this.employees.filter(e => {
          if (e.fields.Sessions) {
            return !e.fields.Sessions.includes(this.session.id)
          } else if (e.fields.Status !== 'Inactive') {
            return true
          } else {
            return false
          }
        }).map(e => {
          return {
            value: e.id,
            text: `${e.fields.Name} ${e.fields.Department}`,
            name: e.fields.Name,
            title: e.fields.Title,
            dept: e.fields.Department
          }
        })
      },
      attendeesToAdd () {
        const empSelect = this.employeesToAdd
        
        const deptSelect = this.employees.filter(e => {
          return this.departmentsToAdd.includes(e.fields.Department) && 
                 e.fields.Status !== 'Inactive'
        })
        
        const locSelect = this.employees.filter(e => {
          return this.locationsToAdd.includes(e.fields['Location Formatted']) &&
                 e.fields.Status !== 'Inactive'
        })
        
        const otherSelect = this.employees.filter(e => {
          const og = this.otherGroupsToAdd  // help with readability
          if (og.includes('Did Not Attend This Curriculum')) {
            return (e.fields['Curriculums Checked In'] && !e.fields['Curriculums Checked In'].includes(this.session.fields.Curriculum[0])) &&
                   !this.futureAttendeesOfSessionsInCurriculum.includes(e.id)
          }
          if (og.includes('Requires Manager Training')) {
            return !e.fields['Latest Manager Training'] &&
                   this.hasDirectReports(e)
          }
          if (og.includes('Requires Diversity Training')) {
            return !e.fields['Latest Diversity Training']
          }
          if (og.includes('Requires Sexual Harassment Training')) {
            return e.fields['Requires Sexual Harassment Training'] !== 1
          }
          if (og.includes('Requires COVID-19 Training')) {
            return !e.fields['Latest COVID-19 Training']
          }
        })
        
        // these lists are AND selectors
        // attendees who show up in all of them are the ones who get added
        let andSelections = [deptSelect, locSelect, otherSelect]
                            .filter(list => list.length > 0)
        let numSelectionsRequired = andSelections.length
        andSelections = andSelections.flat(1)
        if (!this.includeContingentWorkers) {
          andSelections = andSelections.filter(e => e.fields['Is In ADP'])
        }
        if (this.includeManagersOnly) {
          andSelections = andSelections.filter(e => this.hasDirectReports(e))
        }
        andSelections = andSelections.map(e => e.id)
        let andSelectionsFiltered = andSelections.filter(s => {
          return getNumOccurences(andSelections, s) >= numSelectionsRequired
        })
        // find how many times a value occurs in an array
        function getNumOccurences (array, value) {
          return array.filter((v) => (v === value)).length
        }
        
        const allSelectNotInSession = empSelect.concat(andSelectionsFiltered)
          .filter(e => {
            if (this.session.fields.Attendees) {
              return !this.session.fields.Attendees.includes(e)
            } else {
              return true
            }
          })
        
        const uniqueAttendees = new Set(allSelectNotInSession)
        return [...uniqueAttendees]
      },
      attendeesToAddEmployeeRecords () {
        return this.employees.filter(e => {
          return this.attendeesToAdd.includes(e.id)
        })
      },
      attendeesToRemove () {
        return this.employeesToRemove.map(e => e.id)
      },
      departmentOptions () {
        return this.employees.filter(e => {
          return e.fields.Department && e.fields.Department !== ''
        }).map(e => {
          return {
            text: e.fields.Department,
            value: e.fields.Department
          }
        }).sort((a, b) => {
          return a.text > b.text ? 1 : -1
        })
      },
      futureAttendeesOfSessionsInCurriculum () {
        return this.futureSessionsInCurriculum.filter(s => {
          return s.fields.Attendees
        }).map(s => {
          return s.fields.Attendees
        }).flat()
      },
      futureSessionsInCurriculum () {
        return this.$store.getters.getSessions.filter(s => {
          const isInCurriculum = s.fields.Curriculum[0] === this.session.fields.Curriculum[0]
          const isInFuture = s.fields.Start && new Date(s.fields.Start) >= new Date()
          return isInCurriculum && isInFuture
        })
      },
      locationOptions () {
        return this.employees.filter(e => {
          return e.fields['Location Formatted'] && 
                 e.fields['Location Formatted'] !== ''
        }).map(e => {
          return {
            text: e.fields['Location Formatted'],
            value: e.fields['Location Formatted']
          }
        }).sort((a, b) => {
          return a.text > b.text ? 1 : -1
        })
      },
      requiresManagerTrainingIds () {
        return this.employees.filter(e => {
          return !e.fields['Latest Manager Training'] && 
                 this.hasDirectReports(e)
        }).map(e => e.id)
      },
      removeDisabled () {
        return this.employeesToRemove.length === 0
      },
      saveDisabled () {
        return this.attendeesToAdd.length === 0
      },
      calendarLink () {
        const title = this.session.fields.Name
        const description = this.session.fields.Description || ''
        const location = this.session.fields.Location || ''
        const datesString = moment.utc(this.session.fields.Start)
                            .format().replace(/:|-/g,'') + '%2F' +
                            moment.utc(this.session.fields.End)
                            .format().replace(/:|-/g,'')
        const videoLink = this.session.fields['Video Share Link'] || false
        let details = ''
        if (videoLink) {
          // the new lines in this variable are intentional
          details = `Video Link: ${videoLink}


`
        }
        details += description
        
        return 'https://www.google.com/calendar/render?action=TEMPLATE' + 
               '&text=' + encodeURI(title) +
               '&details=' + encodeURI(details) +
               '&location=' + encodeURI(location) +
               '&dates=' + datesString
      },
    },
    methods: {
      getAvatar (employee) {
        if (employee.fields['Avatar']) {
          return employee.fields['Avatar'][0].url
        } else {
          const color = this.$vuetify.theme.themes.light.primary.split('#')[1]
          return `https://ui-avatars.com/api/?name=${employee.fields.Name}&background=${color}&color=fff&rounded=true`
        }
      },
      hasDirectReports (employee) {
        return this.employees.filter(e => {
          return e.fields['Is In ADP'] && e.fields['Manager Email'] === employee.fields.Email
        }).length > 0
      },
      remove (item, list) {
        const index = this[list].indexOf(item.value)
        if (index >= 0) this[list].splice(index, 1)
      },
      reset () {
        this.employeesToAdd = []
        this.departmentsToAdd = []
        this.locationsToAdd = []
        this.sendInviteEmail = false
        
        this.$store.dispatch('getRecords', {
          table: 'Sessions',
          view: 'All Sessions',
          dispatch: 'setSessions'
        }).then(() => {
          this.$store.dispatch('setSessionAttendeesLoading', false)
        })
        
        this.$store.dispatch('getRecords', {
          table: 'Employees',
          view: 'All Employees',
          dispatch: 'setEmployees'
        })
      },
      removeAttendees () {
        const attendees = this.session.fields.Attendees ? 
                          this.session.fields.Attendees
                          .filter(e => {
                            return !this.attendeesToRemove.includes(e)
                          }) :
                          []
        this.removeLoading = true
        
        this.$store.dispatch('setSessionAttendeesLoading', true)
        this.$store.dispatch('updateRecords', {
          table: 'Sessions',
          records: [{
            id: this.session.id,
            fields: {
              'Attendees': attendees
            }
          }]
        })
        .then(() => {
          this.dialog = false
          this.removeLoading = false
          this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `Removed attendees`
          })
          this.reset()
        })
      },
      save () {
        const attendees = this.session.fields.Attendees ? 
                          this.session.fields.Attendees
                          .concat(this.attendeesToAdd) :
                          this.attendeesToAdd
        this.saveLoading = true
        this.disableSave = true
        
        this.$store.dispatch('setSessionAttendeesLoading', true)
        this.$store.dispatch('updateRecords', {
          table: 'Sessions',
          records: [{
            id: this.session.id,
            fields: {
              'Attendees': attendees
            }
          }]
        })
        .then(() => {
          let snackbarText = 'Added attendees'
          if (this.sendInviteEmail) {
            const emails = this.employees.filter(e => {
              return this.attendeesToAdd.includes(e.id)
            }).map(e => {
              return e.fields.Email
            }).toString()
            const schedLink = process.env.VUE_APP_BASE_URL + '/training/' +
                              this.session.fields.URL[0]
            this.$store.dispatch('sendEmail', {
              to: '',
              bcc: emails,
              subject: `You're signed up for ${this.session.fields.Name}!`,
              body: `Don't forget to <a href="${this.calendarLink}">add this event to your calendar</a>.
                     You can also <a href="${schedLink}">manage your schedule</a> in case you need to switch sessions.`
            })
            snackbarText += ' and sent invite emails'
          }
          this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: snackbarText
          })
          this.dialog = false
          this.saveLoading = false
          this.disableSave = false
          this.reset()
        })
      }
    }
  }
</script>
